import React, { useState, useEffect } from 'react';
import { 
  FiUsers, FiUserCheck, FiShield, FiActivity, FiPlus, 
  FiTrash2, FiClock, FiTrendingUp, FiEye, FiLock, 
  FiAlertCircle, FiUserX, FiRefreshCw, FiZap, FiMail, FiEdit,
  FiCheckCircle, FiAlertTriangle, FiGlobe, // ✅ Ajout de FiGlobe pour la localisation
  FiUserCheck as FiUserActive
} from 'react-icons/fi';
import { FaRocket } from "react-icons/fa";
import api from '../../services/api'; 
import { AdminSidebar } from '../../components/admin';
import { ThreatTypesChart, StatsEvolutionChart } from '../../components/charts/SecurityCharts';
import '../../components/admin/AdminLayout.css';

function AdminDashboard() {
  const userRole = localStorage.getItem('role'); 
  const adminName = localStorage.getItem('name');

  const [stats, setStats] = useState({
    totalAdmins: 0,
    totalPartners: 0,
    totalClients: 0, 
    lockedAdmins: 0,
    newMessages: 0
  });
  
  const [securityLogs, setSecurityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchDashboardData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const resStats = await api.get('/dashboard');
      setStats(resStats.data);

      if (userRole === 'SUPER_ADMIN') {
        const resLogs = await api.get('/admin/logs');
        setSecurityLogs(resLogs.data);
      }
      setLastRefresh(new Date());
    } catch (error) {
      console.error('❌ Erreur dashboard:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(() => fetchDashboardData(false), 60000); 
    return () => clearInterval(interval);
  }, []);

  const formatAction = (action) => {
    const act = action.toUpperCase();
    if (act === 'LOGIN_SUCCESS' || act === 'CONNEXION_REUSSIE') 
        return { label: 'Connexion', color: '#10b981', icon: <FiUserCheck />, priority: 'low' };
    if (act === 'LOGIN_FAILED' || act === 'ECHEC_LOGIN') 
        return { label: 'Échec Login', color: '#ef4444', icon: <FiUserX />, priority: 'high' };
    if (act === 'PASSWORD_RESET_SUCCESS') 
        return { label: 'Mise à jour MDP', color: '#059669', icon: <FiCheckCircle />, priority: 'medium' };
    if (act === 'FORGOT_PASSWORD_REQ') 
        return { label: 'Demande Reset', color: '#3b82f6', icon: <FiMail />, priority: 'low' };
    if (act === 'SECURITY_ALERT') 
        return { label: 'Alerte Sécu', color: '#dc2626', icon: <FiAlertTriangle />, priority: 'high' };
    if (act === 'INVITATION_CREEE') 
        return { label: 'Invitation', color: '#8b5cf6', icon: <FiPlus />, priority: 'low' };
    if (act === 'COMPTE_ACTIVE') 
        return { label: 'Activation', color: '#10b981', icon: <FiUserActive />, priority: 'medium' };
    if (act.includes('CREATION')) 
        return { label: 'Ajout', color: '#10b981', icon: <FiPlus />, priority: 'low' };
    if (act.includes('SUPPRESSION')) 
        return { label: 'Suppression', color: '#ef4444', icon: <FiTrash2 />, priority: 'high' };
    if (act.includes('MODIFICATION')) 
        return { label: 'Modification', color: '#f59e0b', icon: <FiEdit />, priority: 'medium' };
    return { label: action, color: '#64748b', icon: <FiActivity />, priority: 'low' };
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <AdminSidebar />
        <main className="admin-main">
          <div className="loader-box">
             <FiRefreshCw className="spin" size={40} />
             <p className="mt-3 text-muted">Initialisation sécurisée...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <AdminSidebar />
      <main className="admin-main">
        <div className="welcome-bar mb-4">
            <div>
                <h2 className="m-0 fw-bold">Bonjour, {adminName || 'Admin'} 👋</h2>
                <p className="text-muted m-0 small">Tableau de bord de la plateforme S2i.</p>
            </div>
            <div className="refresh-status">
              <FiClock /> <span>{lastRefresh.toLocaleTimeString()}</span>
              <div className="pulse-indicator"></div>
            </div>
        </div>

        <div className="stats-row">
            <div className="stat-card-mini">
                <FiMail className="icon-msg" />
                <div className="val">{stats.newMessages}</div>
                <div className="lab">Messages Neufs</div>
            </div>
            <div className="stat-card-mini">
                <FiUserCheck className="icon-part" />
                <div className="val">{stats.totalPartners}</div>
                <div className="lab">Partenaires</div>
            </div>
            <div className="stat-card-mini">
                <FiUsers className="icon-cli" />
                <div className="val">{stats.totalClients}</div>
                <div className="lab">Clients</div>
            </div>
            <div className="stat-card-mini">
                <FiShield className="icon-adm" />
                <div className="val">{stats.totalAdmins}</div>
                <div className="lab">Admins</div>
            </div>
            <div className="stat-card-mini">
                <FiLock className="icon-sec" />
                <div className="val">{stats.lockedAdmins}</div>
                <div className="lab">Alertes Sécu</div>
            </div>
        </div>

        {userRole === 'SUPER_ADMIN' ? (
          <>
            <div className="charts-grid">
              <div className="dashboard-section shadow-sm">
                <h3 className="section-title"><FiTrendingUp color="#4caf50" /> Croissance</h3>
                <div style={{ height: '250px' }}><StatsEvolutionChart /></div>
              </div>
              <div className="dashboard-section shadow-sm">
                <h3 className="section-title"><FiZap color="#ffc107" /> Menaces</h3>
                <div style={{ height: '250px' }}><ThreatTypesChart data={securityLogs} /></div>
              </div>
            </div>

            <div className="dashboard-section shadow-sm">
              <h3 className="section-title d-flex justify-content-between align-items-center">
                <span><FiActivity color="#9c27b0" className="me-2"/> Journal d'Audit Trail</span>
                <span className="badge-count">{securityLogs.length} actions</span>
              </h3>
              <div className="audit-feed mt-3">
                {securityLogs.length > 0 ? securityLogs.slice(0, 8).map((log, i) => {
                  const cfg = formatAction(log.action);
                  return (
                    <div key={i} className={`audit-item ${cfg.priority}`}>
                      <div className="audit-icon" style={{ background: cfg.color }}>{cfg.icon}</div>
                      <div className="audit-content">
                        <div className="audit-header">
                            <span className="audit-label" style={{color: cfg.color}}>{cfg.label}</span>
                            <div className="d-flex align-items-center gap-2">
                                {/* ✅ AFFICHAGE DU LIEU SI DISPONIBLE */}
                                {log.location && (
                                    <span className="audit-location">
                                        <FiGlobe className="me-1"/>{log.location}
                                    </span>
                                )}
                                <span className="audit-time">{new Date(log.timestamp).toLocaleTimeString()}</span>
                            </div>
                        </div>
                        <div className="audit-text">
                            <b>{log.adminEmail}</b> : {log.details}
                        </div>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-4 text-muted small">Aucun log récent.</div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="welcome-section shadow-sm text-center">
             <FaRocket size={50} color="#d90000" className="mb-3" />
             <h3 className="fw-bold">Session Gestionnaire Active</h3>
             <p className="text-muted">Utilisez le menu latéral pour gérer les données de S2i.</p>
          </div>
        )}
      </main>

      <style>{`
        .admin-main { padding: 2rem; background: #f4f7fe; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .welcome-bar { display: flex; justify-content: space-between; align-items: center; }
        .stats-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1rem; margin-bottom: 2rem; }
        .stat-card-mini { background: white; padding: 1.2rem; border-radius: 20px; text-align: center; box-shadow: 0 4px 12px rgba(0,0,0,0.03); border: 1px solid #edf2f7; transition: 0.3s; }
        .stat-card-mini:hover { transform: translateY(-5px); border-color: #d90000; }
        .stat-card-mini .val { font-size: 1.8rem; font-weight: 800; color: #2d3748; margin: 5px 0; }
        .stat-card-mini .lab { font-size: 0.7rem; color: #718096; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
        .icon-msg { color: #ef4444; } .icon-part { color: #10b981; } .icon-cli { color: #3b82f6; } .icon-adm { color: #8b5cf6; } .icon-sec { color: #f59e0b; }
        .charts-grid { display: grid; grid-template-columns: 1.5fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem; }
        .dashboard-section { background: white; padding: 1.5rem; border-radius: 20px; border: 1px solid #e2e8f0; }
        .section-title { font-size: 1rem; font-weight: 800; margin-bottom: 1rem; color: #1e293b; text-transform: uppercase; }
        .audit-feed { display: flex; flex-direction: column; gap: 8px; }
        .audit-item { display: flex; align-items: center; gap: 12px; padding: 10px 15px; background: #f8fafc; border-radius: 12px; border: 1px solid transparent; }
        .audit-item.high { border-left: 4px solid #ef4444; }
        .audit-icon { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 0.9rem; flex-shrink: 0; }
        .audit-content { flex: 1; }
        .audit-header { display: flex; justify-content: space-between; align-items: center; }
        .audit-label { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; }
        .audit-time { font-size: 0.65rem; color: #94a3b8; }
        .audit-location { font-size: 0.65rem; color: #3b82f6; font-weight: 600; display: flex; align-items: center; }
        .audit-text { font-size: 0.8rem; color: #475569; margin-top: 2px; }
        .loader-box { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 70vh; }
        .spin { animation: spin 1s linear infinite; color: #d90000; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .refresh-status { display: flex; align-items: center; gap: 8px; background: white; padding: 6px 12px; border-radius: 30px; font-size: 0.75rem; font-weight: 600; color: #64748b; border: 1px solid #e2e8f0; }
        .pulse-indicator { width: 8px; height: 8px; background: #10b981; border-radius: 50%; animation: pulse 2s infinite; }
        @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); } 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); } }
        .badge-count { font-size: 0.7rem; background: #f1f5f9; color: #475569; padding: 4px 10px; border-radius: 10px; }
      `}</style>
    </div>
  );
}

export default AdminDashboard;